import {FormControlLabel, Radio, RadioGroup} from "@mui/material";
import {Consts} from "../pages/consts";
import {useContext, useRef, useState} from "react";
import AsyncSelect from "react-select/async";
import {checkDrugForDoubleEntry, extractDrugStrengthInfo} from "../pages/utils";
import { strengthUnits } from "../../resources/json/jsons";
import {useTranslation} from "react-i18next";
import useMmcService from "../../services/MmcService";
import InitContext from "../pages/InitContext";

const SearchDrug = ({patDrugs, allProductNames, allSubstances, onSetDrug, onResetFields, onSetMessageText,
                      onSetStrength, onSetStrengthUnit, language}) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const [drugSearchKind, setDrugSearchKind] = useState(Consts.SUBSTANCE);
  const [selectedValue, setSelectedValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const { clearError, getDrugByProductNameJson } = useMmcService();
  const selectContainerRef = useRef(null);

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      const options = Array.from({ length: 33000 }, (_, i) => ({
        value:
          drugSearchKind === Consts.PRODUCT
            ? allProductNames[i]
            : allSubstances[i],
        label:
          drugSearchKind === Consts.PRODUCT
            ? allProductNames[i]
            : allSubstances[i]?.replace("##", ": ")?.replaceAll('&&&', ` ${t("and")} `),
      }))?.filter((option) =>
        option?.label?.toLowerCase()?.startsWith(inputValue?.toLowerCase()),
      );

      const allOptions = options?.length > 450 ? [...options?.slice(0, 450), ...options?.slice(450)] : options;
      callback(allOptions?.slice(0, 500));
    }, 500);
  };

  function handleChangingSearchKind(e) {
    onSetMessageText("");
    setSelectedValue(null);
    setInputValue("");
    setDrugSearchKind(e.target.value);
  }

  const resetFields = () => {
    onSetMessageText("");
    onSetDrug({});
    onResetFields();
  }

  const focusInputAtEnd = () => {
    window.requestAnimationFrame(() => {
      const input = selectContainerRef.current?.querySelector('input');

      if (!input) {
        return;
      }

      const textLength = input.value?.length ?? 0;
      input.focus();
      input.setSelectionRange(textLength, textLength);
    });
  };

  const hasExactDropdownMatch = (value) => {
    const normalizedValue = value?.trim()?.toLowerCase();

    if (!normalizedValue) {
      return false;
    }

    const optionsSource = drugSearchKind === Consts.PRODUCT
      ? allProductNames
      : allSubstances;

    return optionsSource?.some((optionValue) => {
      const optionLabel = drugSearchKind === Consts.PRODUCT
        ? optionValue
        : optionValue?.replace("##", ": ")?.replaceAll('&&&', ` ${t("and")} `);

      return optionLabel?.trim()?.toLowerCase() === normalizedValue;
    });
  };

  const handleInputChange = (newValue, { action }) => {
    if (action !== 'input-change') {
      return inputValue;
    }

    if (selectedValue?.label && newValue !== selectedValue.label) {
      setSelectedValue(null);
      resetFields();
    }

    setInputValue(newValue);
    return newValue;
  };

  const handleFocus = () => {
    if (inputValue) {
      focusInputAtEnd();
    }
  };

  const handleMouseUp = () => {
    window.requestAnimationFrame(() => {
      const input = selectContainerRef.current?.querySelector('input');

      if (!input || document.activeElement !== input) {
        return;
      }

      if (!selectedValue?.label || inputValue !== selectedValue.label) {
        return;
      }

      const selectionStart = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;

      if (selectionStart === 0 && selectionEnd === 0) {
        setSelectedValue(null);
        setInputValue("");
        resetFields();
      }
    });
  };

  const handleBlur = () => {
    window.requestAnimationFrame(() => {
      const currentInputValue = selectContainerRef.current?.querySelector('input')?.value ?? inputValue;
      const normalizedCurrentInputValue = currentInputValue?.trim();

      if (!normalizedCurrentInputValue) {
        return;
      }

      if (selectedValue?.label?.trim()?.toLowerCase() === normalizedCurrentInputValue.toLowerCase()) {
        return;
      }

      if (!hasExactDropdownMatch(normalizedCurrentInputValue)) {
        setSelectedValue(null);
        setInputValue("");
        resetFields();
      }
    });
  };

  const handleSelectDrug = (drug) => {
    if (!drug) {
      setSelectedValue(null);
      setInputValue("");
      resetFields();
      return;
    }

    resetFields();
    let productName = drug?.value;
    let alreadyExists = false;

    let labelForSelect = drug?.value?.indexOf('##') >= 0
      ? drug?.value?.replace("##", ', ')?.replaceAll('&&&', ` ${t("and")} `)
      : drug?.value;

    setSelectedValue({ label: labelForSelect, value: drug?.value });
    setInputValue(labelForSelect);

    if (drugSearchKind === Consts.SUBSTANCE) {
      const parts = drug?.value?.split('##');
      productName = parts[1];
      alreadyExists = checkDrugForDoubleEntry(patDrugs, labelForSelect);

      if (alreadyExists) {
        onSetMessageText(t("drug_already_added"));
        setSelectedValue(null);
        setInputValue("");
      } else {
        onSetMessageText("");
      }
    }

    if(!alreadyExists){
      clearError();

      getDrugByProductNameJson(context?.token, productName, language)
        .then((data) => {
          // activeSubstance in patDrugs are strings, in data (new drug) is a string
          alreadyExists = checkDrugForDoubleEntry(patDrugs, data?.productName);

          if (alreadyExists) {
            onSetMessageText(t("drug_already_added"));
          } else {
            onSetMessageText("");
            const [strength, strengthUnit] = extractDrugStrengthInfo(productName);
            onSetStrength(strength);
            onSetStrengthUnit(strengthUnit);

            onSetDrug({
              ...data,
              strength: strength,
              strengthUnit: strengthUnits?.find(
                (option) => option?.value === strengthUnit,
              )?.label,
              frequency: 1,
              frequencyUnit: "day(s)",
              timingType: Consts.DAY_TIMES,
              status: Consts.ACTIVE,
              intakes: Consts.DEFAULT_DAY_TIME_INTAKES
            });
          }
        })
        .catch((message) => onSetMessageText(message));
    }
  };


  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#0078d4' : '#ccc',
      boxShadow: state.isFocused
        ? '0 0 0 2px rgba(0, 120, 212, 0.2)'
        : 'none',
      '&:hover': {
        borderColor: '#0078d4'
      }
    }),
    input: (base) => ({
      ...base,
      color: 'inherit',
      caretColor: '#0078d4',
      opacity: 1,
      visibility: 'visible'
    })
  };


  return (
    <div className="add-drug-search-grid add-drug-search-compact">
      <div className="add-drug-search-options">
        <RadioGroup
          row
          aria-labelledby="product-substance-group"
          name="product-substance-group"
          value={drugSearchKind}
          defaultValue={drugSearchKind}
          onChange={handleChangingSearchKind}
        >
          <FormControlLabel
            value={Consts.SUBSTANCE}
            control={<Radio />}
            label={<label>{t("active_substance")}</label>}
          />
          <FormControlLabel
            value={Consts.PRODUCT}
            control={<Radio />}
            label={<label>{t("product_name")}</label>}
          />
        </RadioGroup>
      </div>
      <div className="add-drug-search-select" ref={selectContainerRef} onMouseUp={handleMouseUp}>
        <AsyncSelect
          key={drugSearchKind} // triggers re-rendering
          id="drug"
          backspaceRemovesValue={false}
          blurInputOnSelect={false}
          cacheOptions
          controlShouldRenderValue={false}
          inputValue={inputValue}
          loadOptions={loadOptions}
          onChange={handleSelectDrug}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onInputChange={handleInputChange}
          defaultOptions
          openMenuOnFocus
          placeholder={t("select")}
          className="custom-select"
          classNamePrefix="custom-select"
          styles={customStyles}
          value={selectedValue}
        />
      </div>
    </div>
  );
}

export default SearchDrug;